import React, { useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Input } from 'components/shared/Input';
import Button from 'components/shared/Button';
import CountrySelect from 'components/shared/CountrySelect';
import styles from './ProfileEditNew.module.scss';
import AllSpecialistsHeader from 'modules/salon/pages/AllSpecialists/AllSpecialistsHeader';
import Modal from 'components/shared/modals/Modal';
import useProfile from 'sdk/hooks/user/useProfile';
import useUser from 'sdk/hooks/user/useUser';
import supabase, { cloudApi } from 'sdk/api';
import Avatar from 'components/shared/Avatar';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/cropImage';
import useUpdateUser from 'sdk/hooks/user/useUpdateUser';
import addAvatar from 'images/new-icons/addAvatar.svg';
import { ReactComponent as IconEditPen } from 'images/new-icons/edit-pen.svg';
import { ReactComponent as IconCalendar } from 'images/calendar.svg';
import useValidateUserFields from 'sdk/hooks/user/useValidateUserFields';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useTranslation } from 'react-i18next';

function ProfileEdit() {
  const {t} = useTranslation() as any
  const { data: profile, refetch: refetchProfile } = useProfile();
  const { data: isAuthData } = useIsAuth();
  const userData = useUser();
  const updateUser = useUpdateUser({ id: userData?.data?.user?.id || '' });
  const {
    isUsernameExists,
    isEmailExists,
    isEmailFormatValid,
    isPhoneFormatValid,
    isValidating,
    checkUsernameExists,
    checkEmailExists,
    validateEmailFormat,
    validatePhoneFormat,
  } = useValidateUserFields({
    userId: userData?.data?.user?.id || '',
    currentUsername: profile?.username,
    currentEmail: profile?.email,
    currentPhone: profile?.phone,
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    birthday: '',
    phone: '+966',
    email: '',
    country: 'SA',
    state: '',
    zip_code: '',
  });
  const [isNameValid, setIsNameValid] = useState<boolean>(true);

  const isEmailFieldDisabled = isAuthData?.user?.app_metadata?.provider === 'google';

  const validateName = (name: string) => {
    const re = /^[a-zA-Z\u0600-\u06FF\s]+$/;
    return re.test(name) && name.length <= 30 && name.trim().length > 0;
  };
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [avatarImage, setAvatarImage] = useState<string>();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [tempAvatarUrl, setTempAvatarUrl] = useState<string>();
  const [tempBgUrl, setTempBgUrl] = useState<string>();

  // Debounce timers
  const usernameDebounceRef = useRef<NodeJS.Timeout>();
  const emailDebounceRef = useRef<NodeJS.Timeout>();
  const phoneDebounceRef = useRef<NodeJS.Timeout>();

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
      if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (profile) {
      const profileEmail = profile.email || isAuthData?.user?.email || isAuthData?.user?.user_metadata?.email || '';

      // Fallback: если first_name/last_name пустые, берём из user_metadata (Google OAuth)
      const metaFullName = userData?.data?.user?.user_metadata?.full_name || 
                           userData?.data?.user?.user_metadata?.name || '';
      const metaParts = metaFullName.trim().split(/\s+/);
      const metaFirstName = metaParts[0] || '';
      const metaLastName = metaParts.slice(1).join(' ') || '';

      setFormData(prev => {
        const newData = {
          first_name: profile.first_name || metaFirstName,
          last_name: profile.last_name || metaLastName,
          username: profile.username || '',
          birthday: profile.birthday || '',
          phone: profile.phone ? (profile.phone.startsWith('+') ? profile.phone : `+${profile.phone}`) : '+966',
          email: profileEmail,
          country: profile.country || 'SA',
          state: profile.state || '',
          zip_code: profile.zip_code?.toString() || '',
        };
        
        // Only update if data actually changed
        if (JSON.stringify(prev) === JSON.stringify(newData)) {
          return prev;
        }
        return newData;
      });
    }
  }, [profile?.id, profile?.email, profile?.username, profile?.phone, profile?.birthday, isAuthData?.user?.email]);

  const handleFieldChange = (field: string, value: string) => {
    if (field === 'phone') {
      // Ensure phone always starts with +966
      if (!value) {
        setFormData(prev => ({ ...prev, [field]: '+966' }));
        return;
      }

      // If user tries to delete the +966 prefix, prevent it
      if (!value.startsWith('+966')) {
        const cleanedValue = value.replace(/\D/g, '');
        // If they deleted part of 966, restore it
        if (cleanedValue.length < 3 || !cleanedValue.startsWith('966')) {
          setFormData(prev => ({ ...prev, [field]: '+966' }));
          return;
        }
        setFormData(prev => ({ ...prev, [field]: `+${cleanedValue}` }));
        
        // Debounce phone validation
        if (phoneDebounceRef.current) {
          clearTimeout(phoneDebounceRef.current);
        }
        phoneDebounceRef.current = setTimeout(() => {
          validatePhoneFormat(cleanedValue);
        }, 500);
        return;
      }

      const cleanedValue = value.substring(1).replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [field]: `+${cleanedValue}` }));
      
      // Debounce phone validation
      if (phoneDebounceRef.current) {
        clearTimeout(phoneDebounceRef.current);
      }
      phoneDebounceRef.current = setTimeout(() => {
        validatePhoneFormat(cleanedValue);
      }, 500);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Debounce email validation
      if (field === 'email') {
        if (emailDebounceRef.current) {
          clearTimeout(emailDebounceRef.current);
        }
        emailDebounceRef.current = setTimeout(() => {
          const isValid = validateEmailFormat(value);
          if (isValid) {
            checkEmailExists(value);
          }
        }, 500);
      }
    }
    setModifiedFields(prev => new Set(prev).add(field));
  };

  const handleNameChange = async (value: string) => {
    if (value.length <= 30) {
      setFormData(prev => ({ ...prev, username: value }));
      setIsNameValid(validateName(value));
      setModifiedFields(prev => new Set(prev).add('username'));

      // Debounce username validation
      if (usernameDebounceRef.current) {
        clearTimeout(usernameDebounceRef.current);
      }
      
      if (validateName(value)) {
        usernameDebounceRef.current = setTimeout(() => {
          checkUsernameExists(value);
        }, 500);
      }
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    const imageUrl = URL.createObjectURL(file);
    setAvatarImage(imageUrl);
    setIsAvatarModalOpen(true);
    e.target.value = '';
  };

  const saveCroppedAvatar = useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(avatarImage, croppedAreaPixels);

      const response = await cloudApi.uploadMedia(croppedImage);

      setTempAvatarUrl(response);
      setTempBgUrl(response);

      setAvatarImage(undefined);
      setIsAvatarModalOpen(false);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, avatarImage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (updateUser.isLoading || isValidating) {
      return;
    }

    if (isNameValid === false) {
      setIsNameValid(false);
      return;
    }

    if (isUsernameExists || isEmailExists || !isEmailFormatValid || !isPhoneFormatValid) {
      return;
    }

    const updateData: Record<string, any> = {};

    if (tempAvatarUrl) {
      updateData.avatar = tempAvatarUrl;
      updateData.bg_cover = tempBgUrl;
    }

    if (!profile?.country) {
      updateData.country = 'SA';
    }

    modifiedFields.forEach(field => {
      if (field === 'zip_code') {
        updateData[field] = formData[field] ? parseInt(formData[field]) : null;
      } else if (field === 'email') {
        updateData.new_email = formData[field];
      } else if (field === 'phone') {
        updateData[field] = formData[field].replace('+', '');
      } else {
        updateData[field] = formData[field].trim();
      }
    });

    try {
      const { data, error } = await supabase.auth.updateUser({
        phone: formData.phone,
      });
      if (error) {
        console.error('Error updating user:', error);
      } else {
        console.log('User updated:', data);
      }

      await updateUser.mutateAsync(updateData);
      const refreshedData = await refetchProfile();

      if (refreshedData) {
        setTempAvatarUrl(undefined);
        setTempBgUrl(undefined);
        setModifiedFields(new Set());
        handleBack();
      }
    } catch (error) {
      console.error('Detailed error:', error);
    }
  };

  const handleBack = () => {
    setTempAvatarUrl(undefined);
    setTempBgUrl(undefined);
    setModifiedFields(new Set());
    window.history.back();
  };

  return (
    <div className={styles.profileContainer}>
      <AllSpecialistsHeader
        edit
        onClose={handleBack}
        text="Personal"
        hasAvatar={Boolean(tempAvatarUrl || profile?.avatar)}
      />

      <form
        className={styles.form}
        onSubmit={e => {
          handleSubmit(e);
        }}
      >
        <div className={styles.section}>
          <div className={styles.profileImg}>
            <img src={tempBgUrl || profile?.bg_cover} alt={profile?.username} />
          </div>

          <input
            type="file"
            id="avatarUpload"
            className={styles.fileInput}
            onChange={handleAvatarUpload}
            accept="image/*"
            hidden
          />
          <label htmlFor="avatarUpload" className={styles.avatar}>
            {!tempAvatarUrl && !profile?.avatar ? (
              <img src={addAvatar} alt="add avatar" className={styles.noAvatar} />
            ) : (
              <Avatar
                image={tempAvatarUrl || profile?.avatar}
                isExpert={profile?.expert_id}
                className={styles.avatarImage}
              />
            )}
            {/* <div className={styles.uploadOverlay}>
              <IconUploadPhoto />
            </div> */}
          </label>

          <Modal
            onClose={() => setIsAvatarModalOpen(false)}
            isOpen={isAvatarModalOpen}
            className={styles.cropperModal}
          >
            <div className={styles.cropContainer}>
              {avatarImage && (
                <Cropper
                  image={avatarImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <Button onClick={saveCroppedAvatar}>{t("Save")}</Button>
          </Modal>

          <h2 className={styles.sectionTitle}>{t("Information")}</h2>

          <div className={styles.inputGroup}>
            <Input
              value={formData.username}
              onChangeValue={value => handleNameChange(value)}
              placeholder="Name*"
              className={styles.input}
              style={{
                border:
                  !isNameValid || isUsernameExists ? '1px solid #FF3B30' : '1px solid #01010180',
              }}
              error={!isNameValid || isUsernameExists}
            />
            {!isNameValid && (
              <div className={styles.error}>
                {t("Name should contain only English and Arabic letters and spaces (up to 30 characters)")}
              </div>
            )}
            {isUsernameExists && (
              <div className={styles.error}>
                {t("This username already exists. Please choose another one.")}
              </div>
            )}
            <Input
              type="date"
              value={formData.birthday ? formData.birthday.split('T')[0] : ''}
              onChangeValue={value => handleFieldChange('birthday', value)}
              placeholder="MM.DD.YYYY"
              className={clsx(styles.input, styles.dateInput)}
              icon={null}
            />
            <Input
              // suffix={<IconEditPen />}
              type="tel"
              value={formData.phone || '+966'}
              onChangeValue={value => handleFieldChange('phone', value)}
              placeholder="+966 XX XXX XXXX"
              className={styles.input}
              error={!isPhoneFormatValid}
              style={{
                border: !isPhoneFormatValid ? '1px solid #FF3B30' : '1px solid #01010180',
              }}
            />
            {!isPhoneFormatValid && formData.phone && (
              <div className={styles.error}>{t("Please enter a valid phone number")}</div>
            )}
            <Input
              suffix={isEmailFieldDisabled ? null : <IconEditPen />}
              type="email"
              value={formData.email}
              onChangeValue={value => handleFieldChange('email', value)}
              placeholder="Email*"
              className={styles.input}
              icon={null}
              error={isEmailExists || !isEmailFormatValid}
              style={{
                border:
                  isEmailExists || !isEmailFormatValid
                    ? '1px solid #FF3B30'
                    : '1px solid #01010180',
                opacity: isEmailFieldDisabled ? 0.5 : 1,
                pointerEvents: isEmailFieldDisabled ? 'none' : 'auto',
              }}
              disabled={isEmailFieldDisabled}
            />
            {!isEmailFormatValid && formData.email && (
              <div className={styles.error}> {t("Please enter a valid email address")}</div>
            )}
            {isEmailExists && (
              <div className={styles.error}>
                {t("This email already exists. Please choose another one.")}
              </div>
            )}

          </div>

          <div className={styles.infoText}>
            <div className={styles.infoIcon} />
            <div className={styles.infoText_text}>
              {t('Your name and country will will be publicly available')}
            </div>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button
            type="submit"
            darkGreen
            wide
            disabled={
              updateUser.isLoading ||
              isValidating ||
              isUsernameExists ||
              isEmailExists ||
              !isNameValid ||
              !isEmailFormatValid ||
              !isPhoneFormatValid ||
              !formData.phone ||
              formData.phone === '+' ||
              formData.phone === '+966' ||
              !formData.email ||

              !formData.username ||
              !formData.birthday
            }
          >
            Save
          </Button>
          <Button ligtGrey wide onClick={handleBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ProfileEdit;
